import React, { useState, useEffect } from 'react';
import { Star, Check, X, Trash2, Eye, EyeOff, Loader2, Gift, MessageSquare } from 'lucide-react';
import { Heading, BodyText, SmallText } from '../../../components/brand/BrandTypography';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialsTab = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all' | 'pending' | 'approved'
    const [actionLoading, setActionLoading] = useState(null);

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const fetchTestimonials = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/admin/testimonials`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Failed to fetch testimonials');
            
            const data = await response.json();
            setTestimonials(data.testimonials || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (testimonialId) => {
        setActionLoading(testimonialId);
        try {
            const token = localStorage.getItem('co_regulate_token');
            await fetch(`${API_URL}/api/admin/testimonials/${testimonialId}/approve`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            setTestimonials(prev => prev.map(t => 
                t.id === testimonialId ? { ...t, is_approved: true } : t
            ));
        } catch (err) {
            console.error('Failed to approve:', err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (testimonialId) => {
        setActionLoading(testimonialId);
        try {
            const token = localStorage.getItem('co_regulate_token');
            await fetch(`${API_URL}/api/admin/testimonials/${testimonialId}/reject`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            setTestimonials(prev => prev.map(t => 
                t.id === testimonialId ? { ...t, is_approved: false } : t
            ));
        } catch (err) {
            console.error('Failed to reject:', err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (testimonialId) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
        
        setActionLoading(testimonialId);
        try {
            const token = localStorage.getItem('co_regulate_token');
            await fetch(`${API_URL}/api/admin/testimonials/${testimonialId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            setTestimonials(prev => prev.filter(t => t.id !== testimonialId));
        } catch (err) {
            console.error('Failed to delete:', err);
        } finally {
            setActionLoading(null);
        }
    };

    const filteredTestimonials = testimonials.filter(t => {
        if (filter === 'pending') return !t.is_approved;
        if (filter === 'approved') return t.is_approved;
        return true;
    });

    const pendingCount = testimonials.filter(t => !t.is_approved && t.allow_publish).length;
    const approvedCount = testimonials.filter(t => t.is_approved).length;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#1D6B6B]" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <Heading as="h2" size="text-xl" className="text-[#1D6B6B]">
                        User Testimonials
                    </Heading>
                    <SmallText className="text-[#7D8E95] mt-1">
                        Review and approve user testimonials for public display
                    </SmallText>
                </div>
                
                {/* Stats */}
                <div className="flex gap-4">
                    <div className="px-4 py-2 rounded-xl bg-yellow-50 border border-yellow-200">
                        <SmallText className="text-yellow-700 font-medium">
                            {pendingCount} Pending
                        </SmallText>
                    </div>
                    <div className="px-4 py-2 rounded-xl bg-green-50 border border-green-200">
                        <SmallText className="text-green-700 font-medium">
                            {approvedCount} Approved
                        </SmallText>
                    </div>
                </div>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2">
                {[
                    { id: 'all', label: 'All' },
                    { id: 'pending', label: 'Pending Review' },
                    { id: 'approved', label: 'Approved' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setFilter(tab.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filter === tab.id
                                ? 'bg-[#1D6B6B] text-white'
                                : 'bg-white text-[#7D8E95] hover:bg-[#1D6B6B]/10'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Testimonials list */}
            {filteredTestimonials.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-[#FFBB98]/30">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-[#7D8E95]/40" />
                    <BodyText className="text-[#7D8E95]">
                        {filter === 'pending' ? 'No pending testimonials' : 
                         filter === 'approved' ? 'No approved testimonials yet' :
                         'No testimonials submitted yet'}
                    </BodyText>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredTestimonials.map(testimonial => (
                        <div 
                            key={testimonial.id}
                            className={`p-6 rounded-2xl border transition-all ${
                                testimonial.is_approved 
                                    ? 'bg-green-50/50 border-green-200'
                                    : 'bg-white border-[#FFBB98]/30'
                            }`}
                            data-testid={`testimonial-item-${testimonial.id}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    {/* User info and rating */}
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center text-white font-medium">
                                            {testimonial.user_name?.charAt(0).toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <SmallText className="font-medium text-[#1D6B6B]">
                                                {testimonial.user_name}
                                            </SmallText>
                                            <div className="flex items-center gap-2">
                                                <div className="flex gap-0.5">
                                                    {[1, 2, 3, 4, 5].map(star => (
                                                        <Star 
                                                            key={star}
                                                            className={`w-4 h-4 ${
                                                                star <= testimonial.rating
                                                                    ? 'text-[#E4D3A3] fill-[#E4D3A3]'
                                                                    : 'text-[#E4D3A3]/30'
                                                            }`}
                                                        />
                                                    ))}
                                                </div>
                                                <SmallText className="text-[#7D8E95]">
                                                    • {new Date(testimonial.created_at).toLocaleDateString()}
                                                </SmallText>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Testimonial text */}
                                    <BodyText className="text-[#344648] mb-3 italic">
                                        "{testimonial.text}"
                                    </BodyText>

                                    {/* Meta info */}
                                    <div className="flex flex-wrap gap-2">
                                        {testimonial.allow_publish ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#1D6B6B]/10 text-xs text-[#1D6B6B]">
                                                <Eye className="w-3 h-3" />
                                                Can be published
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#7D8E95]/10 text-xs text-[#7D8E95]">
                                                <EyeOff className="w-3 h-3" />
                                                Private feedback
                                            </span>
                                        )}
                                        
                                        {testimonial.discount_code && (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-yellow-100 text-xs text-yellow-700">
                                                <Gift className="w-3 h-3" />
                                                {testimonial.discount_code}
                                            </span>
                                        )}
                                        
                                        {testimonial.is_approved && (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-100 text-xs text-green-700">
                                                <Check className="w-3 h-3" />
                                                Approved
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2">
                                    {actionLoading === testimonial.id ? (
                                        <Loader2 className="w-5 h-5 animate-spin text-[#7D8E95]" />
                                    ) : (
                                        <>
                                            {!testimonial.is_approved && testimonial.allow_publish && (
                                                <button
                                                    onClick={() => handleApprove(testimonial.id)}
                                                    className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
                                                    title="Approve for public display"
                                                >
                                                    <Check className="w-5 h-5" />
                                                </button>
                                            )}
                                            
                                            {testimonial.is_approved && (
                                                <button
                                                    onClick={() => handleReject(testimonial.id)}
                                                    className="p-2 rounded-lg bg-yellow-100 hover:bg-yellow-200 text-yellow-700 transition-colors"
                                                    title="Remove from public display"
                                                >
                                                    <EyeOff className="w-5 h-5" />
                                                </button>
                                            )}
                                            
                                            <button
                                                onClick={() => handleDelete(testimonial.id)}
                                                className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
                                                title="Delete testimonial"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TestimonialsTab;
